import { useState } from 'react';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconUpload } from '@components/icons/upload';
import type {
  ExistingList,
  ImportMode,
  ImportedOption,
} from '@components/integrations/integrations-types';
import { hideModal } from '@components/modal';
import { RadioCardGroup } from '@components/radio-card';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  ErrorValidatingCSV,
  ImportToolStepper,
} from 'ui/authenticated/main/pages/bulk-import/common';

export type ImportOptionsStepProps = Pick<ExistingList, 'existingValues' | 'onApply'> & {
  options: ImportedOption[];
  errorList: string[];
  onBack: () => void;
};

export const ImportOptionsStep = ({
  options,
  errorList,
  existingValues,
  onApply,
  onBack,
}: ImportOptionsStepProps) => {
  const { t } = useScopedTranslate(locale);
  const hasExistingOptions = existingValues.length > 0;
  const [mode, setMode] = useState<ImportMode>(hasExistingOptions ? 'add' : 'replace');
  const hasErrors = errorList.length > 0;
  const existingValueSet = new Set(existingValues);
  const newCount = options.filter((option) => {
    return !existingValueSet.has(option.value);
  }).length;
  const skippedCount = options.length - newCount;
  const importedValueSet = new Set(options.map((option) => option.value));
  const removedCount = existingValues.filter((value) => !importedValueSet.has(value)).length;
  const showRemovalWarning = mode === 'replace' && removedCount > 0 && !hasErrors;
  const canApply = !hasErrors && options.length > 0;
  const replaceSummary = hasExistingOptions
    ? t('summaryReplace', { count: options.length })
    : t('summaryEmpty', { count: options.length });
  const summary = mode === 'add' ? t('summaryAdd', { newCount, skippedCount }) : replaceSummary;

  return (
    <Stack space={6}>
      <ImportToolStepper currentIndex={3} />
      {hasErrors ? (
        <ErrorValidatingCSV errorList={errorList} />
      ) : (
        <Stack justify="center" align="center" space={6}>
          <IconCheckmark color="elementGreen100" size={64} />
          <Stack align="center">
            <Text font="bold16" text={t('validated')} />
            <Text font="regular14" color="textSecondary" text={summary} />
          </Stack>
        </Stack>
      )}
      {hasExistingOptions && !hasErrors && (
        <RadioCardGroup
          label={t('mode')}
          value={mode}
          onChange={(next) => setMode(next as ImportMode)}
          options={[
            { value: 'add', label: t('modeAdd'), helpText: t('modeAddHelp') },
            { value: 'replace', label: t('modeReplace'), helpText: t('modeReplaceHelp') },
          ]}
        />
      )}
      {showRemovalWarning && (
        <Alert variant="warning">
          <Stack space={2} orientation="horizontal" align="center">
            <IconFormIndicatorWarning color="elementOrange100" />
            <Text
              font="regular14"
              color="elementOrange200"
              text={t('removalWarning', { count: removedCount })}
            />
          </Stack>
        </Alert>
      )}
      <Stack orientation="horizontal" justify="space-between">
        <Button variant="secondary" text={t('core.back')} onClick={onBack} />
        <Stack space={4} orientation="horizontal">
          <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
          <Button
            variant="primary-blue"
            text={t('applyToList')}
            iconRight={<IconUpload />}
            isDisabled={!canApply}
            onClick={() => {
              onApply(options, mode);
              hideModal();
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    validated: 'Validated!',
    summaryAdd:
      '{newCount} new option(s) will be added · {skippedCount} already exist and will be left unchanged',
    summaryReplace: '{count} option(s) will replace the current options',
    summaryEmpty: '{count} option(s) will be added to the list',
    mode: 'What should happen to the current options?',
    modeAdd: 'Add new items',
    modeAddHelp: 'Options whose value already exists are left unchanged',
    modeReplace: 'Replace entire list',
    modeReplaceHelp: 'Options not in the file are removed',
    removalWarning:
      '{count} current option(s) not in the file will be removed. Fields and forms using this list will show the new options after you save.',
    applyToList: 'Apply to list',
  },
};
